import Image from 'next/image'
import styles from './SelectedDay.module.scss'
import Link from 'next/link'
import { Button, Heading, Paragraph } from '~elements'
import { DATE_FORMAT, DATE_FORMATTER, NEXT_IMAGE_QUALITY } from '~constants'
import dayjs from 'utils/dayjs'
import { useEffect, useMemo, useState } from 'react'
import { SelectedDayProps } from 'types/calendar'
import { FormattedEvent } from 'components/modules/Calendar/types/Event'
import { getCtaData, getEverySphereFlagDescription } from 'components/modules/Calendar/utils'
import { CtaData } from 'components/modules/Calendar/types'
import { isExternalLink } from 'utils'
import CloseIcon from '/public/assets/svg/close-thick.svg'

const SelectedDay = ({
  activeShowId,
  disclaimer,
  eventInfo,
  groupTicketsOfferText,
  isOpen,
  onClose,
  onTimeSelect
}: SelectedDayProps) => {
  const artist = eventInfo?.artistDetail
  const events = useMemo(
    () => (eventInfo?.events ?? []).filter(e => e.artistId === artist?.artistId),
    [eventInfo?.events, artist?.artistId]
  )

  const currentEventDate = events[0] ? dayjs(events[0].dateTime, DATE_FORMAT.DATE_TIME) : null
  const formattedDate = currentEventDate?.format(DATE_FORMATTER.FULL_DATE) ?? ''
  const activeFormattedDate = currentEventDate?.format('MM/DD/YYYY') ?? ''
  const linkModule = 'calendar_sliding_panel'
  const [activeEvent, setActiveEvent] = useState<null | FormattedEvent>(null)
  const [ctaData, setCtaData] = useState<CtaData>({ title: null, url: null })

  const handleEscapeKey = (event: { key: string }) => {
    if (event?.key === 'Escape' && onClose) {
      onClose()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [])

  useEffect(() => {
    if (events.length > 0 && activeShowId) {
      const foundEvent = events.find(e => e.id === activeShowId) ?? null
      setActiveEvent(foundEvent)
      const ctaData = getCtaData(foundEvent, artist)
      setCtaData(ctaData)
    } else {
      setCtaData(getCtaData(null, artist))
    }
  }, [events, activeShowId])

  const flags = getEverySphereFlagDescription(events)
  return (
    <>
      <div className={`${styles['selected-event']} ${isOpen ? styles['selected-event--is-open'] : ''}`}>
        <div className={styles['selected-event__image-container']}>
          <button className={styles['close-button']} onClick={onClose} aria-label='Close'>
            <CloseIcon />
          </button>
          {artist?.edpPage?.slug && (
            <Link className={styles['selected-event__image-link']} href={artist?.edpPage?.slug} target='_blank'>
              {artist?.artistName && <Heading level={4}>{artist?.artistName}</Heading>}
            </Link>
          )}
          {artist?.calendarEventDetailsImage?.url && (
            <Image
              src={artist?.calendarEventDetailsImage?.url}
              alt={artist?.calendarEventDetailsImage?.altText || ''}
              style={{ objectFit: 'cover' }}
              fill
              quality={NEXT_IMAGE_QUALITY}
            />
          )}
        </div>
        <div className={styles['selected-event__copy-container']}>
          <div className={styles['selected-event__date-times-container']}>
            <div className={styles['selected-event__date']}>{formattedDate}</div>
            <div className={styles['selected-event__event-times']}>
              {events
                .filter(item => item?.artistId == artist?.artistId)
                .map(data => {
                  const formattedTime = dayjs(data.dateTime, DATE_FORMAT.DATE_TIME).format('h:mm A')
                  // If the time has sphere flags, add an asterisk to indicate
                  // special conditions
                  const titleWithMarker =
                    data.sphereFlags && data.sphereFlags.length > 0 ? `${formattedTime}*` : formattedTime

                  return (
                    <Button
                      key={data.id}
                      color='light'
                      fill={activeShowId === data.id ? 'solid' : 'hollow'}
                      size='sm'
                      title={titleWithMarker}
                      onClick={() => {
                        onTimeSelect(data.id)
                        setActiveEvent(data)
                      }}
                      analytics={{ skipEvent: true }}
                    />
                  )
                })}
            </div>
            <div className={styles['selected-event__sphere-flags']}>
              {flags.map(flag => (
                // If there are any sphere flags, display them below the event times, but only once
                <Paragraph key={flag} text={{ html: `<p>*${flag}</p>` }} />
              ))}
            </div>
          </div>
          <div className={styles['selected-event__cta-section']}>
            <div className={styles['selected-event__ctas']}>
              <Button
                isTargetBlank={true}
                disabled={events.length === 0}
                isFullWidth={true}
                color='primary'
                fill='solid'
                size='lg'
                justify='left'
                url={ctaData.url}
                analytics={{
                  linkModule: linkModule,
                  linkPosition: '1',
                  clickTracking: `${linkModule}:${artist?.artistName}:${'buy-tickets'}`,
                  eventData: {
                    ticketmasterEventId: activeEvent?.id || '',
                    eventDate: activeFormattedDate
                  }
                }}
                title={ctaData.title ?? ''}
              />
              {artist?.ctaSecondaryTitle && artist?.ctaSecondaryUrl && (
                <Button
                  isTargetBlank={isExternalLink(artist.ctaSecondaryUrl)}
                  disabled={false}
                  isFullWidth={true}
                  color='gold'
                  fill='solid'
                  size='lg'
                  url={artist.ctaSecondaryUrl}
                  analytics={{
                    linkModule: linkModule,
                    linkPosition: '2',
                    clickTracking: `${linkModule}:${artist?.artistName}:${artist.ctaSecondaryTitle}`,
                    eventData: {
                      ticketmasterEventId: activeEvent?.id || '',
                      eventDate: activeFormattedDate
                    }
                  }}
                  title={artist.ctaSecondaryTitle}
                />
              )}
            </div>
            {artist?.shouldOfferGroupTickets && (
              <div className={styles['selected-event__group-tickets-offer']}>
                {groupTicketsOfferText?.html && <Paragraph text={groupTicketsOfferText} justify='center' />}
              </div>
            )}
            {disclaimer?.html && (
              <div className={styles['selected-event__disclaimer']}>
                <Paragraph fontSize='xs' justify='center' text={disclaimer} />
              </div>
            )}
          </div>
        </div>
      </div>
      {isOpen && <div className={styles['overlay']} onClick={onClose}></div>}
    </>
  )
}

export default SelectedDay
